import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Draws a tile layer's rendering quadrants (`tile_map_layer.cpp:412-566`). Each quadrant is one
 * canvas item per run of tiles that share a material and `z_index` (`:313-376`), each with its own
 * light list, as one batched mesh per run of cells that share an atlas source.
 */
import { useMemo } from 'react';
import { quadrantCanvasItems } from '../resources/tileset/tileCanvasItems';
import { LitCanvasItemPixels } from './components/LitCanvasItemPixels';
import { CanvasItemGroup, useCanvasItemKey } from './components/CanvasItemGroup';
import { useCanvasItemMaterialFile } from './components/canvasItemMaterialContext';
import { multiplyModulate } from './canvasItemModulate';
import { useCanvasModulateFor } from './canvasModulate';
import { useGodotLinearColor } from './godotColor';
import { canvasKeyAtZ } from './canvasPaintOrder';
import { accumulateCanvasItemZ } from './lighting2d/canvasItemPlacement';
import { TileSourceMesh } from './TileSourceMesh';
/** Draws inside the layer's canvas key (`useCanvasItemKey`), from which a tile `z_index` moves an item. */
export function TileQuadrants({ quadrants, ...layer }) {
    const items = useMemo(() => drawnItems(quadrants), [quadrants]);
    // Keyed by draw index: an item has no identity across a re-parse that moves its cells.
    return items.map((item, drawIndex) => _jsx(TileItem, { item: item, layer: layer }, drawIndex));
}
function drawnItems(quadrants) {
    let firstOrder = 0;
    return quadrants.flatMap(quadrantCanvasItems).map((item) => {
        const drawn = { ...item, firstOrder };
        firstOrder += item.runs.length;
        return drawn;
    });
}
function TileItem({ item, layer }) {
    const material = useTileItemMaterial(item.material, layer.material);
    // `z_as_relative` (`:356`), so the item's z accumulates onto the layer's.
    const zFinal = accumulateCanvasItemZ(layer.zFinal, { z_index: item.zIndex });
    const canvasKey = canvasKeyAtZ(useCanvasItemKey(), layer.zFinal, zFinal);
    const own = multiplyModulate(layer.selfTint, useCanvasModulateFor(material));
    const color = useGodotLinearColor(own);
    return (_jsx(CanvasItemGroup, { renderOrder: canvasKey, children: _jsx(LitCanvasItemPixels, { material: material, lightMask: layer.lightMask, zFinal: zFinal, children: (pixelMaterial) => item.runs.map(({ source, cells }, runIndex) => (_jsx(TileSourceMesh, { source: source, cells: cells, grid: layer.model, renderOrder: item.firstOrder + runIndex, color: color, opacity: own.a, name: layer.name, pixelMaterial: pixelMaterial, sampler: layer.sampler }, runIndex))) }) }));
}
/** A tile's own material replaces the layer's: the item stops `use_parent_material` (`:350`). */
function useTileItemMaterial(own, layerMaterial) {
    const fromFile = useCanvasItemMaterialFile(own && 'path' in own ? own.path : null);
    if (!own)
        return layerMaterial;
    return 'path' in own ? fromFile : own.properties;
}
//# sourceMappingURL=TileQuadrants.js.map